import ACTION_TYPES from '../../constants/actionTypes';
import { setSentryUserContext } from 'shared/lib/utils';
import * as Sentry from '@sentry/browser';
import * as analytics from 'shared/lib/analytics';
import api from '../../api';
import axios from 'axios';
import useLoginToken from '../../services/useLoginToken';

/*
 * Sign in
 * ========================================
 */

function signInRequest() {
  return {
    type: ACTION_TYPES.SIGN_IN,
  };
}

function signInSuccess(data) {
  setSentryUserContext(data.account);
  analytics.identify(data.account);
  analytics.track('Login Succeeded', {
    category: 'user',
    label: 'email',
    nonInteraction: 1,
  });
  axios.defaults.headers.common['Authorization'] = `Bearer ${data.auth.token}`;

  return {
    type: ACTION_TYPES.SIGN_IN_SUCCESS,
    payload: data,
  };
}

function magicLinkSignInSuccess(data) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${data.auth.token}`;
  return {
    type: ACTION_TYPES.MAGIC_LINK_SIGN_IN_SUCCESS,
    payload: data,
  };
}

function signInFailure(error) {
  analytics.track('Login Failed', {
    category: 'user',
    label: 'email',
    nonInteraction: 1,
  });
  return {
    type: ACTION_TYPES.SIGN_IN_FAILURE,
    error,
  };
}

export function signIn(email, password) {
  return dispatch => {
    dispatch(signInRequest());
    return new Promise((resolve, reject) => {
      api
        .signIn({ email, password })
        .then(resp => {
          dispatch(signInSuccess(resp));
          resolve(resp);
        })
        .catch(error => {
          dispatch(signInFailure(error));
          reject(error);
        });
    });
  };
}

export function magicLinkSignIn(token) {
  return dispatch => {
    dispatch(signInRequest());
    return new Promise((resolve, reject) => {
      useLoginToken(token)
        .then(({ data }) => {
          dispatch(magicLinkSignInSuccess(data));
          return resolve(data);
        })

        .catch(error => {
          console.log({ error });
          dispatch(signInFailure(error));
          reject(error);
        });
    });
  };
}

function facebookOauthLogin() {
  return new Promise((resolve, reject) => {
    window.FB.login(
      response => {
        if (response.authResponse) {
          resolve(
            response.authResponse.accessToken,
            response.authResponse.userID
          );
        } else {
          reject(response);
        }
      },
      {
        scope: 'public_profile,email',
      }
    );
  });
}

export function signInWithFacebook() {
  return dispatch => {
    dispatch(signInRequest());
    return new Promise((resolve, reject) => {
      facebookOauthLogin()
        .then(fbAccessToken => {
          return api.logInWithFacebook(fbAccessToken);
        })
        .then(response => {
          analytics.track('Login Succeeded', {
            category: 'user',
            label: 'facebook',
            nonInteraction: 1,
          });
          dispatch(signInSuccess(response));
          resolve(response);
        })
        .catch(error => {
          dispatch(signInFailure(error));
          reject(error);
        });
    });
  };
}

/*
 * Reset password
 * ========================================
 */

function sendResetPasswordEmailRequest() {
  analytics.track('Homepage - Send Password Reset Email', { category: 'user' });
  return {
    type: ACTION_TYPES.SEND_RESET_PASSWORD_EMAIL,
  };
}

function sendResetPasswordEmailSuccess() {
  analytics.track('Homepage - Send Password Reset Email Success', {
    category: 'user',
    nonInteraction: 1,
  });
  return {
    type: ACTION_TYPES.SEND_RESET_PASSWORD_EMAIL_SUCCESS,
  };
}

function sendResetPasswordEmailFailure(error) {
  analytics.track('Homepage - Send Password Reset Email Failure', {
    category: 'user',
    nonInteraction: 1,
  });
  return {
    type: ACTION_TYPES.SEND_RESET_PASSWORD_EMAIL_FAILURE,
    error,
  };
}

export function sendResetPasswordEmail(email) {
  return dispatch => {
    dispatch(sendResetPasswordEmailRequest());
    return new Promise((resolve, reject) => {
      api
        .sendResetPasswordEmail(email)
        .then(() => {
          dispatch(sendResetPasswordEmailSuccess());
          resolve();
        })
        .catch(error => {
          dispatch(sendResetPasswordEmailFailure(error));
          reject(error);
        });
    });
  };
}

function sendChangePasswordRequest() {
  analytics.track('Homepage - Reset Password', { category: 'user' });
  return {
    type: ACTION_TYPES.RESET_PASSWORD,
  };
}

function sendChangePasswordSuccess() {
  analytics.track('Homepage - Reset Password Success', {
    category: 'user',
    nonInteraction: 1,
  });
  return {
    type: ACTION_TYPES.RESET_PASSWORD_SUCCESS,
  };
}

function sendChangePasswordFailure(error) {
  analytics.track('Homepage - Reset Password Failure', {
    category: 'user',
    nonInteraction: 1,
  });
  return {
    type: ACTION_TYPES.RESET_PASSWORD_FAILURE,
    error,
  };
}

export function resetPassword(token, password) {
  return dispatch => {
    dispatch(sendChangePasswordRequest());
    return new Promise((resolve, reject) => {
      api
        .submitResetPassword({ token, password })
        .then(() => {
          dispatch(sendChangePasswordSuccess());
          resolve();
        })
        .catch(error => {
          dispatch(sendChangePasswordFailure(error));
          reject(error);
        });
    });
  };
}

/*
 * Verify email address
 * ========================================
 */

function verifyEmailRequest() {
  analytics.track('Portal - Verify Email', { category: 'user' });
  return {
    type: ACTION_TYPES.VERIFY_EMAIL,
  };
}

function verifyEmailSuccess() {
  analytics.track('Portal - Verify Email Success', {
    category: 'user',
    nonInteraction: 1,
  });
  return {
    type: ACTION_TYPES.VERIFY_EMAIL_SUCCESS,
  };
}

function verifyEmailFailure(error) {
  analytics.track('Portal - Verify Email Failure', {
    category: 'user',
    nonInteraction: 1,
  });
  return {
    type: ACTION_TYPES.VERIFY_EMAIL_FAILURE,
    error,
  };
}

export function verifyEmail(token) {
  return dispatch => {
    dispatch(verifyEmailRequest());
    return new Promise((resolve, reject) => {
      api
        .verifyEmailAddressToken(token)
        .then(() => {
          dispatch(verifyEmailSuccess());
          resolve();
        })
        .catch(error => {
          dispatch(verifyEmailFailure(error));
          reject(error);
        });
    });
  };
}

/*
 * Sign up
 * ========================================
 */

function signUpRequest() {
  return {
    type: ACTION_TYPES.SIGN_UP,
  };
}

function signUpSuccess(data) {
  setSentryUserContext(data.account);
  analytics.identify(data.account);
  analytics.track('Sign Up Succeeded', {
    category: 'user',
    label: 'email',
    nonInteraction: 1,
  });
  axios.defaults.headers.common['Authorization'] = `Bearer ${data.auth.token}`;

  return {
    type: ACTION_TYPES.SIGN_UP_SUCCESS,
    payload: data,
  };
}

function signUpFailure(error) {
  analytics.track('Sign Up Failed', {
    category: 'user',
    label: 'email',
    nonInteraction: 1,
  });

  return {
    type: ACTION_TYPES.SIGN_UP_FAILURE,
    error,
  };
}

export function signUp(information) {
  return dispatch => {
    const opts = {
      email: information.email,
      password: information.password,
      firstName: information.firstName,
      lastName: information.lastName,
    };

    dispatch(signUpRequest());
    return new Promise((resolve, reject) => {
      api
        .signUp(opts)
        .then(resp => {
          dispatch(signUpSuccess(resp.data));
          resolve(resp.data);
        })
        .catch(error => {
          dispatch(signUpFailure(error.response.data.error));
          reject(error.response.data.error);
        });
    });
  };
}

/*
 * Sign out
 * ========================================
 */

export function signOut() {
  Sentry.configureScope(scope => {
    scope.setUser();
  });
  delete axios.defaults.headers.common['Authorization'];
  return {
    type: ACTION_TYPES.SIGN_OUT,
  };
}
